/**
 * RPC Engine Service Factory — creates an EngineService from a generic
 * transport function (`call`) plus transport-specific hooks.
 *
 * Both `engine-http.ts` (HTTP/WS) and `engine-tauri.ts` (Tauri IPC)
 * delegate to this factory, providing only their transport layer and
 * event subscription mechanism.
 *
 * This eliminates ~30 identical one-liner method delegations that were
 * previously duplicated across every EngineService implementation.
 */

import type {
    EngineNotificationHandler,
    EngineService,
    EngineStatus,
} from "../platform";
import {
    ENGINE_PROTOCOL_VERSION,
    Methods,
    type RequestMethodName,
} from "@magic-md/engine-client/protocol";

/* ── Options ── */

export interface RpcEngineServiceOptions {
    /**
     * Generic transport function: send an RPC call and return the result.
     * Each implementation wraps its own transport (HTTP POST, Tauri IPC, etc.).
     */
    call: <T>(method: RequestMethodName, params?: unknown) => Promise<T>;

    /** Implementation-specific engine status check */
    getStatus: () => Promise<EngineStatus>;

    /** Implementation-specific notification subscription */
    listenNotifications: (
        handler: EngineNotificationHandler,
    ) => Promise<() => void>;

    /** Implementation-specific cleanup */
    stopListening: () => void;

    /** Stable logical client identity used for shared Engine session leases. */
    clientId?: string;

    /** Dynamic identity source for transports that learn or rotate an ID. */
    getClientId?: () => string | undefined;
}

/* ── Factory ── */

/**
 * Create a full EngineService by mapping every protocol method to a `call()`.
 *
 * The returned object can be spread to override host-specific behavior.
 */
export function createRpcEngineService(
    opts: RpcEngineServiceOptions,
): EngineService {
    const { call, getStatus } = opts;
    const getSessionClientId = () => opts.getClientId?.() ?? opts.clientId;
    const withSessionClient = <T extends object>(params: T) => {
        const clientId = getSessionClientId();
        return clientId === undefined ? params : { ...params, clientId };
    };

    return {
        // ── Status ──
        getEngineStatus: getStatus,

        // ── Initialize ──
        initialize: (clientInfo, capabilities) =>
            call("initialize", {
                clientInfo,
                capabilities,
                protocolVersion: ENGINE_PROTOCOL_VERSION,
            }),

        // ── Session ──
        sessionCreate: (params) =>
            call("session/create", withSessionClient(params)),
        sessionClose: (sessionId) =>
            call("session/close", withSessionClient({ sessionId })),
        sessionSync: (params) =>
            call("session/sync", withSessionClient(params)),
        sessionList: () => call("session/list", withSessionClient({})),
        sessionGetState: (params) =>
            call("session/getState", withSessionClient(params)),
        sessionGetOutput: (params) =>
            call("session/getOutput", withSessionClient(params)),
        sessionExplain: (params) =>
            call("session/explain", withSessionClient(params)),
        sessionWatch: (params) =>
            call("session/watch", withSessionClient(params)),
        sessionUnwatch: (params) =>
            call("session/unwatch", withSessionClient(params)),

        // ── Execution ──
        executionStart: (params) => call("execution/start", params),
        executionGetState: (params) => call("execution/getState", params),
        executionCancel: (params) => call("execution/cancel", params),

        // ── Runtime Jobs ──
        jobList: (params) => call("job/list", params ?? {}),
        jobGetState: (params) => call("job/getState", params),
        jobCancel: (params) => call("job/cancel", params),

        // ── Plugin ──
        pluginList: () => call("plugin/list", {}),
        pluginApiCall: (params) => call("plugin/apiCall", params),
        smartIntakePropose: (params) => call("smart-intake/propose", params),
        smartIntakeBuildPlan: (params) => call("smart-intake/buildPlan", params),

        // ── Config ──
        configGet: (params) => call("config/get", params ?? {}),
        configSet: (params) => call("config/set", params),
        configGetSchema: (params) => call("config/getSchema", params ?? {}),
        configReload: (params) => call("config/reload", params ?? {}),

        // ── Sidecar ──
        sidecarList: () => call("sidecar/list", {}),
        sidecarShutdown: (sidecarId) => call("sidecar/shutdown", { sidecarId }),
        sidecarSpawn: (sidecarId, command, args) =>
            call("sidecar/spawn", { sidecarId, command, args }),
        sidecarCall: (sidecarId, method, params) =>
            call("sidecar/call", { sidecarId, method, params }),
        sidecarCallStart: (params) => call("sidecar/callStart", params),

        // ── Notifications ──
        listenNotifications: (handler) =>
            opts.listenNotifications((notification) => {
                const sourceClientId =
                    notification.method === Methods.EventDocumentChanged &&
                    notification.params !== null &&
                    typeof notification.params === "object" &&
                    "sourceClientId" in notification.params
                        ? (notification.params as { sourceClientId?: unknown })
                              .sourceClientId
                        : undefined;
                const clientId = getSessionClientId();
                if (
                    typeof sourceClientId === "string" &&
                    clientId !== undefined &&
                    sourceClientId === clientId
                ) {
                    return;
                }
                handler(notification);
            }),
        stopListening: opts.stopListening,

        // ── Push ──
        pushScan: (params) => call("push/scan", params),
        pushConnect: (params) => call("push/connect", params),
        pushDisconnect: () => call("push/disconnect", {}),
        pushPair: (params) => call("push/pair", params),
        pushSend: (params) => call("push/send", params),
        pushSyncStart: (params) => call("push/syncStart", params),
        pushSyncStop: () => call("push/syncStop", {}),
        pushStatus: () => call("push/status", {}),

        // ── MCP ──
        mcpStart: (params) => call("mcp/start", params ?? {}),
        mcpStop: () => call("mcp/stop", {}),
        mcpStatus: () => call("mcp/status", {}),
        mcpLogs: (params) => call("mcp/logs", params ?? {}),

        // ── Extension ──
        extensionList: () => call("extension/list", {}),
        extensionGetManifest: (params) => call("extension/getManifest", params),
        extensionActivateRuntime: (params) =>
            call("extension/activateRuntime", params),
        extensionInvoke: (params) => call("extension/invoke", params),
        extensionDisposeRuntime: (params) =>
            call("extension/disposeRuntime", params),
        extensionSettingsUpdate: (params) =>
            call("extension/settings.update", params),

        // ── Search ──
        searchQuery: (params) => call("search/query", params),
        searchCancel: (params) => call("search/cancel", params),

        // ── Intelligence ──
        intelligenceComplete: (params) => call("intelligence/complete", params),
        intelligenceHover: (params) => call("intelligence/hover", params),
        intelligenceDiagnostics: (params) => call("intelligence/diagnostics", params),
        intelligenceDefinition: (params) => call("intelligence/definition", params),
        intelligenceReferences: (params) => call("intelligence/references", params),
        intelligenceRename: (params) => call("intelligence/rename", params),
        intelligenceSymbols: (params) => call("intelligence/symbols", params),
        intelligenceSemanticTokens: (params) => call("intelligence/semanticTokens", params),
        intelligenceCodeAction: (params) => call("intelligence/codeAction", params),
        intelligenceInlayHint: (params) => call("intelligence/inlayHint", params),

        // ── Workspace ──
        workspaceReadDir: (params) => call("workspace/readDir", params),
        workspaceReadFile: (params) => call("workspace/readFile", params),
        workspaceWriteFile: (params) => call("workspace/writeFile", params),
        workspaceStat: (params) => call("workspace/stat", params),
        workspaceExists: (params) => call("workspace/exists", params),
        workspaceMkdir: (params) => call("workspace/mkdir", params),
        workspaceRename: (params) => call("workspace/rename", params),
        workspaceRemove: (params) => call("workspace/remove", params),
        workspaceCopy: (params) => call("workspace/copy", params),
        workspaceMove: (params) => call("workspace/move", params),
        workspaceReferenceImpact: (params) => call("workspace/referenceImpact", params),
        workspaceGetInfo: (params) => call("workspace/getInfo", params ?? {}),
        workspaceAddFolder: (params) => call("workspace/addFolder", params),
        workspaceRemoveFolder: (params) => call("workspace/removeFolder", params),
        workspaceListFolders: (params) => call("workspace/listFolders", params ?? {}),
        workspaceSave: (params) => call("workspace/save", params),
        workspaceLoad: (params) => call("workspace/load", params),
        workspaceStarList: (params) => call("workspace/star.list", params ?? {}),
        workspaceStarAdd: (params) => call("workspace/star.add", params),
        workspaceStarRemove: (params) => call("workspace/star.remove", params),
        workspaceStarUpdate: (params) => call("workspace/star.update", params),
        workspaceBookmarkList: (params) => call("workspace/bookmark.list", params ?? {}),
        workspaceBookmarkAdd: (params) => call("workspace/bookmark.add", params),
        workspaceBookmarkRemove: (params) => call("workspace/bookmark.remove", params),
        workspaceBookmarkUpdate: (params) => call("workspace/bookmark.update", params),
        workspaceSessionGet: (params) => call("workspace/session.get", params ?? {}),
        workspaceSessionSet: (params) => call("workspace/session.set", params),

        // ── Publish ──
        publishStart: (params) => call("publish/start", params),
        publishGetState: (params) => call("publish/getState", params),
        publishCancel: (params) => call("publish/cancel", params),
        publishSiteStart: (params) => call("publish/startSite", params),
        publishSiteGetState: (params) => call("publish/siteState", params),
        publishSiteCancel: (params) => call("publish/siteCancel", params),

        // ── History (Snapshots) ──
        historySnapshot: (params) => call("history/snapshot", params),
        historyList: (params) => call("history/list", params),
        historyGet: (params) => call("history/get", params),
        historyDiff: (params) => call("history/diff", params),
        historyRestore: (params) => call("history/restore", params),
        historyDelete: (params) => call("history/delete", params),
        historyTag: (params) => call("history/tag", params),
        historyPrune: (params) => call("history/prune", params),
    };
}
